/**
 * Mousai Commerce — cart drawer controller.
 *
 * The ONLY script allowed to mutate the cart or the drawer DOM. All state is
 * server-rendered: every mutation POSTs to the Cart AJAX API, then re-fetches
 * the cart-drawer section (?sections=cart-drawer) and swaps the region nodes.
 * Mutations are serialized through a single queue so responses can never
 * arrive out of order and overwrite fresher state (the old remove→re-add bug).
 */
document.addEventListener('DOMContentLoaded', () => {
  const SECTION_ID = 'cart-drawer';
  const REGIONS = ['CartDrawerTitle', 'CartDrawerContent', 'CartDrawerFooterWrap'];
  const OPT_OUT_KEY = 'tranquira:protection-opt-out';
  const root = window.Shopify?.routes?.root || '/';

  const drawer = () => document.getElementById('CartDrawer');
  const overlay = () => document.getElementById('CartDrawerOverlay');
  const footData = () => document.getElementById('CartDrawerFooterWrap')?.dataset || {};
  const isOpen = () => drawer()?.classList.contains('is-open');

  // Session-scoped opt-out: toggling protection off is remembered until the
  // tab session ends; a new session starts ON again.
  const optedOut = () => {
    try { return sessionStorage.getItem(OPT_OUT_KEY) === '1'; } catch { return false; }
  };
  const setOptOut = (on) => {
    try { on ? sessionStorage.setItem(OPT_OUT_KEY, '1') : sessionStorage.removeItem(OPT_OUT_KEY); } catch {}
  };

  /* ------------------------------------------------------------- requests */
  async function cartRequest(path, body) {
    const res = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {})
    });
    if (!res.ok) throw new Error(`${path} failed: ${await res.text()}`);
    return res.json();
  }

  const addVariant = (id, quantity) => cartRequest('cart/add.js', { items: [{ id, quantity }] });
  const changeLine = (key, quantity) => cartRequest('cart/change.js', { id: key, quantity });

  async function renderCart() {
    const res = await fetch(`${root}?sections=${SECTION_ID}`, { cache: 'no-store' });
    if (!res.ok) throw new Error('Section fetch failed');
    const html = (await res.json())[SECTION_ID];
    if (!html) throw new Error('Section not found in response');

    const fresh = new DOMParser().parseFromString(html, 'text/html');
    REGIONS.forEach((id) => {
      const newEl = fresh.getElementById(id);
      const oldEl = document.getElementById(id);
      if (newEl && oldEl) oldEl.replaceWith(newEl);
    });

    const count = parseInt(footData().visibleCount || '0', 10);
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
    drawer()?.classList.toggle('is-empty', count === 0);
  }

  /**
   * Shipping-protection invariants, enforced after every render:
   *  - on by default: added whenever the cart goes from empty to having items
   *    (unless the customer switched it off this session),
   *  - never more than qty 1,
   *  - never left behind in an otherwise empty cart.
   */
  async function enforceProtectionRules() {
    for (let i = 0; i < 2; i++) {
      const d = footData();
      const qty = parseInt(d.protectionQty || '0', 10);
      const visible = parseInt(d.visibleCount || '0', 10);
      if (qty > 1 && d.protectionKey) {
        await changeLine(d.protectionKey, 1);
      } else if (qty > 0 && visible === 0 && d.protectionKey) {
        await changeLine(d.protectionKey, 0);
      } else if (qty === 0 && visible > 0 && d.protectionDefault === 'true' && d.protectionVariantId && !optedOut()) {
        await addVariant(Number(d.protectionVariantId), 1);
      } else {
        return;
      }
      await renderCart();
    }
  }

  async function syncCart() {
    await renderCart();
    await enforceProtectionRules();
  }

  /* ---------------------------------------------------- serialized mutations */
  let chain = Promise.resolve();
  function enqueue(op) {
    const run = chain.catch(() => {}).then(async () => {
      drawer()?.classList.add('is-busy');
      try {
        await op();
      } catch (err) {
        console.error('[cart-drawer]', err);
        await syncCart().catch(() => {});
      } finally {
        drawer()?.classList.remove('is-busy');
      }
    });
    chain = run;
    return run;
  }

  /* ---------------------------------------------------------- open / close */
  let lastTrigger = null;

  const openDrawer = () => {
    const el = drawer();
    if (!el || isOpen()) return;
    lastTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    el.classList.add('is-open');
    overlay()?.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    el.querySelector('[data-close-cart]')?.focus({ preventScroll: true });
  };

  const closeDrawer = () => {
    if (!isOpen()) return;
    drawer()?.classList.remove('is-open');
    overlay()?.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    lastTrigger?.focus({ preventScroll: true });
    lastTrigger = null;
  };

  /* --------------------------------------------------------- add to cart */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();

    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;

    enqueue(async () => {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) throw new Error(await res.text());
      await syncCart();
      openDrawer();
    }).finally(() => {
      if (btn) btn.disabled = false;
    });
  });

  /* ------------------------------------------------------ click delegation */
  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      enqueue(async () => {
        await changeLine(remove.dataset.cartRemove, 0);
        await syncCart();
      });
      return;
    }

    const qty = e.target.closest('[data-cart-qty]');
    if (qty) {
      e.preventDefault();
      enqueue(async () => {
        await changeLine(qty.dataset.cartQty, Math.max(0, parseInt(qty.dataset.qty, 10) || 0));
        await syncCart();
      });
      return;
    }

    if (e.target.closest('[data-clear-cart]')) {
      e.preventDefault();
      enqueue(async () => {
        await cartRequest('cart/clear.js');
        await syncCart();
      });
      return;
    }

    if (e.target.closest('[data-open-cart]')) {
      e.preventDefault();
      enqueue(async () => {
        await syncCart();
        openDrawer();
      });
      return;
    }

    if (e.target.closest('[data-close-cart]')) {
      e.preventDefault();
      closeDrawer();
    }
  });

  /* -------------------------------------------------- protection toggle */
  document.addEventListener('change', (e) => {
    const toggle = e.target.closest('[data-protection-toggle]');
    if (!toggle) return;
    const on = toggle.checked;
    toggle.disabled = true;

    enqueue(async () => {
      const d = footData();
      setOptOut(!on);
      if (on) {
        if (parseInt(d.protectionQty || '0', 10) === 0 && d.protectionVariantId) {
          await addVariant(Number(d.protectionVariantId), 1);
        }
      } else if (d.protectionKey) {
        await changeLine(d.protectionKey, 0);
      }
      await syncCart();
    });
  });

  /* ---------------------------------------------- deep link (?cart=open) */
  // /cart redirects here with ?cart=open when the cart has items; open the
  // drawer and strip the param so refresh/share doesn't re-trigger it.
  const params = new URLSearchParams(window.location.search);
  if (params.get('cart') === 'open') {
    params.delete('cart');
    const query = params.toString();
    history.replaceState(
      null,
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash
    );
    // Double rAF: paint one frame in the closed state so the slide-in plays.
    requestAnimationFrame(() => requestAnimationFrame(openDrawer));
  }

  /* ------------------------------------------------- keyboard: esc + trap */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDrawer();
      return;
    }
    if (e.key !== 'Tab' || !isOpen()) return;

    const el = drawer();
    const focusables = [...el.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )].filter((f) => !f.disabled && f.getClientRects().length > 0);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const inside = el.contains(document.activeElement);

    if (e.shiftKey && (document.activeElement === first || !inside)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
      e.preventDefault();
      first.focus();
    }
  });
});
